/**
 * useBoardHeaderZoomDrag - drag KW boundary handles to zoom in/out.
 *
 * The user grabs a vertical KW boundary in the header and drags left/right.
 * Dragging right → week gets wider → zoom in.
 * Dragging left → week gets narrower → zoom out.
 *
 * The dragged boundary follows the cursor via scroll correction
 * applied in useLayoutEffect.
 */

import { useCallback, useRef, useLayoutEffect } from 'react';
import { DAYS_PER_WEEK, BASE_DAY_WIDTH } from '../utils/boardHelpers';
import { useBoardDragListener } from './useBoardDragListener';

const ZOOM_MIN = 0.1;
const ZOOM_MAX = 20.0;

export function useBoardHeaderZoomDrag(
  containerRef: React.RefObject<HTMLDivElement | null>,
  zoomLevel: number,
  onZoomChange?: (zoomLevel: number) => void,
) {
  const startMouseXRef = useRef(0);
  const startDayWidthRef = useRef(0);
  const startScrollLeftRef = useRef(0);
  const anchorDayRef = useRef(0);
  const pendingScrollRef = useRef<number | null>(null);

  const onZoomChangeRef = useRef(onZoomChange);
  const zoomLevelRef = useRef(zoomLevel);
  onZoomChangeRef.current = onZoomChange;
  zoomLevelRef.current = zoomLevel;

  const handleMove = (e: MouseEvent) => {
    const onZoom = onZoomChangeRef.current;
    if (!onZoom) return;

    const deltaX = e.clientX - startMouseXRef.current;
    const startDayWidth = startDayWidthRef.current;

    // Each week grows/shrinks by deltaX pixels
    const newDayWidth = Math.max(
      BASE_DAY_WIDTH * ZOOM_MIN,
      Math.min(BASE_DAY_WIDTH * ZOOM_MAX, startDayWidth + deltaX / DAYS_PER_WEEK),
    );
    const newZoom = newDayWidth / BASE_DAY_WIDTH;

    // Scroll correction: pin the LEFT edge of the KW cell so the
    // dragged boundary (right edge) follows the cursor exactly.
    // pinDay = anchorDay - DAYS_PER_WEEK (left edge of the week)
    // => newScrollLeft = startScrollLeft + pinDay * (newDayWidth - startDayWidth)
    const anchorDay = anchorDayRef.current;
    const pinDay = anchorDay - DAYS_PER_WEEK;
    pendingScrollRef.current = Math.max(
      0,
      startScrollLeftRef.current + pinDay * (newDayWidth - startDayWidthRef.current),
    );

    onZoom(newZoom);
  };

  const { isDragging: isZoomDragging, startDrag } = useBoardDragListener('col-resize', handleMove);

  const handleZoomDragStart = useCallback((anchorDay: number, e: React.MouseEvent) => {
    if (e.button !== 0) return; // left click only
    e.preventDefault();
    e.stopPropagation();

    const container = containerRef.current;
    if (!container || !onZoomChangeRef.current) return;

    startMouseXRef.current = e.clientX;
    startDayWidthRef.current = BASE_DAY_WIDTH * zoomLevelRef.current;
    startScrollLeftRef.current = container.scrollLeft;
    anchorDayRef.current = anchorDay;
    startDrag();
  }, [containerRef, startDrag]);

  // Apply scroll correction after re-render (before paint)
  useLayoutEffect(() => {
    if (pendingScrollRef.current !== null && containerRef.current) {
      containerRef.current.scrollLeft = pendingScrollRef.current;
      pendingScrollRef.current = null;
    }
  });

  return { isZoomDragging, handleZoomDragStart };
}
